import { useQuery } from '@tanstack/react-query';
import { beneficiariesApi } from '../api/modules';
import { Drawer, DrawerCloseButton } from '../components/ui/Drawer';
import { useToast } from '../components/toast/Toast';

interface Props {
  open: boolean;
  code: string | null;
  onClose: () => void;
}

const STAGES = [
  { key: 'applied_at',           label: 'Application',      step: 1 },
  { key: 'lottery_selected_at',  label: 'Lottery selected', step: 2 },
  { key: 'declared_at',          label: 'Declaration',      step: 3 },
  { key: 'deposit_verified_at',  label: 'Deposit verified', step: 4 },
  { key: 'subsidy_released_at',  label: 'Subsidy released', step: 5 },
  { key: 'verified_at',          label: 'Field verified',   step: 6 },
] as const;

export function BeneficiaryDrawer({ open, code, onClose }: Props) {
  const { show } = useToast();
  const { data, isLoading } = useQuery({
    enabled: !!code,
    queryKey: ['beneficiary', code],
    queryFn: () => beneficiariesApi.detail(code!),
  });

  return (
    <Drawer open={open && !!code} onClose={onClose}>
      <div className="bg-navy-800 text-white px-6 py-5 flex items-start justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest opacity-80">Beneficiary · {code}</div>
          <h2 className="serif-display text-2xl font-medium mt-1">{data ? data.name : 'Loading…'}</h2>
          {data && (
            <div className="text-sm opacity-90 mt-2">
              {data.scheme.name} · {data.location.district}{data.location.block && ` · ${data.location.block}`}
            </div>
          )}
        </div>
        <DrawerCloseButton onClick={onClose} />
      </div>

      <div className="px-6 py-5 flex-1 overflow-y-auto space-y-5">
        {isLoading || !data ? (
          <div className="space-y-3">
            <div className="h-20 bg-cream-100 rounded-sm animate-pulse" />
            <div className="h-40 bg-cream-100 rounded-sm animate-pulse" />
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-cream-50 p-3 rounded-sm">
                <div className="text-[10px] uppercase tracking-widest text-navy-800/50">Category</div>
                <div className="text-sm font-medium text-navy-800 mt-1">{data.category}</div>
              </div>
              <div className="bg-cream-50 p-3 rounded-sm">
                <div className="text-[10px] uppercase tracking-widest text-navy-800/50">Subsidy released</div>
                <div className="font-numeric serif-display text-xl text-navy-800 font-medium mt-1">
                  {data.amount_inr > 0 ? '₹' + data.amount_inr.toLocaleString('en-IN') : 'Pending'}
                </div>
              </div>
              <div className="bg-cream-50 p-3 rounded-sm">
                <div className="text-[10px] uppercase tracking-widest text-navy-800/50">Aadhaar</div>
                <div className="font-numeric text-sm text-navy-800 mt-1">{data.aadhaar_masked ?? '—'}</div>
              </div>
              <div className="bg-cream-50 p-3 rounded-sm">
                <div className="text-[10px] uppercase tracking-widest text-navy-800/50">Mobile</div>
                <div className="font-numeric text-sm text-navy-800 mt-1">{data.mobile ?? '—'}</div>
              </div>
            </div>

            <div>
              <h3 className="text-xs uppercase tracking-widest text-navy-800/50 mb-3 font-semibold">Lifecycle progress</h3>
              <div className="relative">
                <div className="absolute left-3 top-3 bottom-3 w-0.5 bg-cream-200" />
                {STAGES.map((s) => {
                  const ts = data.lifecycle[s.key];
                  const reached = !!ts;
                  return (
                    <div key={s.key} className="relative pl-10 pb-4">
                      <div
                        className={`absolute left-0 top-0 w-7 h-7 rounded-full ${
                          reached ? 'bg-saffron-500 text-white' : 'bg-cream-100 text-navy-800/40 border border-cream-300'
                        } flex items-center justify-center text-xs font-medium font-numeric`}
                      >
                        {s.step}
                      </div>
                      <div className={`text-sm font-medium ${reached ? 'text-navy-800' : 'text-navy-800/40'}`}>{s.label}</div>
                      <div className="text-[11px] text-navy-800/60 font-numeric mt-0.5">
                        {reached ? new Date(ts).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : 'Pending'}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {data.pfms_reference && (
              <div className="bg-cream-50 p-3 rounded-sm">
                <div className="text-[10px] uppercase tracking-widest text-navy-800/50">PFMS reference</div>
                <div className="font-numeric text-sm text-navy-800 mt-1">{data.pfms_reference}</div>
              </div>
            )}

            <div>
              <h3 className="text-xs uppercase tracking-widest text-navy-800/50 mb-2 font-semibold">Audit history</h3>
              <div className="space-y-2 text-xs">
                {data.events.map((e, i) => (
                  <div key={i} className="p-3 bg-cream-50 rounded-sm">
                    <div className="font-medium text-navy-800">{e.event_type.replace(/_/g, ' ')}</div>
                    <div className="text-navy-800/50 font-numeric mt-1">
                      {new Date(e.occurred_at).toLocaleString('en-IN')}
                      {e.actor_role_code && ` · ${e.actor_role_code}`}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>

      <div className="border-t border-cream-200 px-6 py-4 flex items-center gap-2 bg-cream-50">
        <button
          type="button"
          onClick={() => show('Lifecycle report queued for PDF export')}
          className="flex-1 bg-navy-800 hover:bg-navy-700 text-white text-sm py-2.5 rounded-sm"
        >
          Export lifecycle PDF
        </button>
        <button
          type="button"
          onClick={() => show('Re-verification requested · Field officer notified')}
          className="px-4 py-2.5 border border-saffron-500 text-saffron-600 hover:bg-saffron-50 text-sm rounded-sm"
        >
          Re-verify
        </button>
      </div>
    </Drawer>
  );
}
